import { useRef, useState } from 'react';

import { cn } from '@/lib/utils';

const toBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const resizeImage = async (file, maxSize = 128) => {
  const img = document.createElement('img');
  const url = URL.createObjectURL(file);
  await new Promise((res) => {
    img.onload = res;
    img.src = url;
  });
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const ratio = Math.min(maxSize / img.width, maxSize / img.height);
  canvas.width = img.width * ratio;
  canvas.height = img.height * ratio;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png', 0.9));
};

const handleFileUpload = async (file) => {
  if (!file) return;

  const resizedBlob = await resizeImage(file, 128);
  const resizedFile = new File([resizedBlob], file.name, {
    type: 'image/png',
  });
  const base64 = await toBase64(resizedFile);

  return base64;
};

const FileComponent = ({
  className = '',
  disabled = false,
  ignoreBase64 = false,
  multiple = false,
  onChange,
  placeholder = 'Drag and drop file here or click to select',
  value: defaultValue,
}) => {
  const inputRef = useRef(null);
  const [value, setValue] = useState({ ...defaultValue });

  const fileText = multiple
    ? value?.files?.map(({ name }) => name)
    : value?.file?.name;

  // Todo: implement multiple base64
  const updateFiles = async (files = []) => {
    const [file] = Array.from(files);

    const base64 = !ignoreBase64 && (await handleFileUpload(file));

    const payload = {
      ...(multiple ? { files: Array.from(files) } : { file }),
      ...(!ignoreBase64 && { base64 }),
    };

    setValue(payload);
    onChange?.(payload);
  };

  const handleDrop = (e) => {
    e.preventDefault();

    updateFiles(e.dataTransfer.files);
  };

  const handleInput = ({ target }) => {
    updateFiles(target.files);
  };

  return (
    <div
      className={cn(
        'flex flex-col px-4 py-8 w-full',
        'bg-background border border-input ring-offset-background rounded-md disabled:cursor-not-allowed disabled:opacity-50',
        'text-base placeholder:text-muted-foreground md:text-sm',
        'cursor-pointer',
        disabled && 'opacity-50 pointer-events-none',
        className
      )}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      role="button"
    >
      <span className="font-semibold">{placeholder}</span>
      <span className="text-xs text-neutral-400">
        {ignoreBase64
          ? '* pdf, docx, xlsx, xls, doc, png, jpg, webp (max 5 MB/file)'
          : '* png, jpg, webp (max 5 MB/file)'}
      </span>
      {fileText &&
        (multiple ? (
          value?.files?.map(({ name, size }) => (
            <span key={name}>
              {name} ({parseInt(size * 0.001)}kb)
            </span>
          ))
        ) : (
          <span>
            {value.file.name} ({parseInt(value.file.size * 0.001)}kb)
          </span>
        ))}
      <input
        type="file"
        hidden
        multiple={multiple}
        onChange={handleInput}
        ref={inputRef}
      />
    </div>
  );
};

export default FileComponent;
